export interface SubscriptionPricePayload {
    amount: number;
    currency_code: string;
}

export interface SubscriptionPeriodPayload {
    unit: string;
    value: number;
}

export interface SubscriptionProductPayload {
    id: string;
    name: string;
    price: SubscriptionPricePayload | null;
    period: SubscriptionPeriodPayload | null;
}

export interface SubscriptionNextProductPayload {
    id: string;
}

export interface SubscriptionPayload {
    status: string;
    product: SubscriptionProductPayload;
    next_product: SubscriptionNextProductPayload | null;
    is_an_offer: boolean;
    auto_renew: boolean;
    has_billing_issues: boolean;
    expiration_date: string | null;
    canceled_at: string | null;
    starts_at: string | null;
    created_at: string | null;
    entitlement_id: string;
    store: string;
}

// SSO-3621: always both stores; `unknown` (and nulls) when the store never reported.
export interface TrialEligibilityPayload {
    store: string;
    status: string;
    source: string | null;
    product_id: string | null;
    checked_at: string | null;
}

export interface UserMembershipPayload {
    active: boolean;
    subscription: SubscriptionPayload | null;
    features: string[];
    trial_eligibility?: TrialEligibilityPayload[];
}

export class UserSubscription {
    status: string;
    product: SubscriptionProductPayload;
    nextProduct: SubscriptionNextProductPayload | null;
    isAnOffer: boolean;
    autoRenew: boolean;
    hasBillingIssues: boolean;
    expirationDate: string | null;
    canceledAt: string | null;
    startsAt: string | null;
    createdAt: string | null;
    entitlementId: string;
    store: string;

    constructor(data: SubscriptionPayload) {
        this.status = data.status;
        this.product = data.product;
        this.nextProduct = data.next_product;
        this.isAnOffer = data.is_an_offer;
        this.autoRenew = data.auto_renew;
        this.hasBillingIssues = data.has_billing_issues;
        this.expirationDate = data.expiration_date;
        this.canceledAt = data.canceled_at;
        this.startsAt = data.starts_at;
        this.createdAt = data.created_at;
        this.entitlementId = data.entitlement_id;
        this.store = data.store;
    }

    fill(data: SubscriptionPayload): this {
        this.status = data.status;
        this.product = data.product;
        this.nextProduct = data.next_product;
        this.isAnOffer = data.is_an_offer;
        this.autoRenew = data.auto_renew;
        this.hasBillingIssues = data.has_billing_issues;
        this.expirationDate = data.expiration_date;
        this.canceledAt = data.canceled_at;
        this.startsAt = data.starts_at;
        this.createdAt = data.created_at;
        this.entitlementId = data.entitlement_id;
        this.store = data.store;
        return this;
    }
}

export class UserMembership {
    active: boolean;
    subscription: UserSubscription | null;
    features: string[];
    trialEligibility: TrialEligibilityPayload[];

    constructor(data?: UserMembershipPayload) {
        this.active = false;
        this.subscription = null;
        this.features = [];
        this.trialEligibility = [];

        if (data) {
            this.fill(data);
        }
    }

    fill(data: UserMembershipPayload): this {
        this.active = data.active ?? false;
        this.subscription = data.subscription ? new UserSubscription(data.subscription) : null;
        this.features = data.features ?? [];
        this.trialEligibility = data.trial_eligibility ?? [];
        return this;
    }

    unset(): this {
        this.active = false;
        this.subscription = null;
        this.features = [];
        this.trialEligibility = [];
        return this;
    }
}
